import React from 'react'
import PropTypes from 'prop-types'
import * as styles from './styles'

const propTypes = {
  variant: PropTypes.oneOf(['standard', 'compact', 'detailed']),
  showTitle: PropTypes.bool,
  customTitle: PropTypes.string,
  showHardwareNote: PropTypes.bool,
  className: PropTypes.string
}

const defaultProps = {
  variant: 'standard',
  showTitle: true,
  customTitle: null,
  showHardwareNote: true,
  className: ''
}

const PerformancePanel = ({ 
  variant, 
  showTitle, 
  customTitle, 
  showHardwareNote, 
  className 
}) => {
  const performanceData = {
    responseTime: {
      range: "20-168ms",
      percentile: "P95 < 200ms",
      description: "Query response time at enterprise scale"
    },
    scale: {
      records: "10M+",
      description: "Tested record capacity with maintained performance"
    },
    accuracy: {
      range: "90-98%",
      average: "96%",
      description: "Typical accuracy across standard ML benchmarks"
    },
    implementation: {
      time: "Hours to days",
      comparison: "vs 3-12 months for traditional ML"
    }
  }

  const title = customTitle || "Enterprise Performance & Accuracy"

  if (variant === 'compact') {
    return (
      <styles.CompactPanel className={className}>
        <styles.MetricsRow>
          <styles.CompactMetric>
            <styles.CompactValue>{performanceData.responseTime.range}</styles.CompactValue>
            <styles.CompactLabel>Response Time</styles.CompactLabel>
          </styles.CompactMetric>
          <styles.CompactMetric>
            <styles.CompactValue>{performanceData.scale.records}</styles.CompactValue>
            <styles.CompactLabel>Records at Scale</styles.CompactLabel>
          </styles.CompactMetric>
          <styles.CompactMetric>
            <styles.CompactValue>{performanceData.accuracy.average}</styles.CompactValue>
            <styles.CompactLabel>Average Accuracy</styles.CompactLabel>
          </styles.CompactMetric>
        </styles.MetricsRow>
      </styles.CompactPanel>
    )
  }

  if (variant === 'detailed') {
    return (
      <styles.DetailedPanel className={className}>
        {showTitle && (
          <styles.PanelTitle>{title}</styles.PanelTitle>
        )}
        
        <styles.DetailedMetricsGrid>
          <styles.DetailedMetric>
            <styles.MetricHeader>Response Time</styles.MetricHeader>
            <styles.MetricValue>{performanceData.responseTime.range}</styles.MetricValue>
            <styles.MetricDetail>{performanceData.responseTime.percentile}</styles.MetricDetail>
            <styles.MetricDescription>{performanceData.responseTime.description}</styles.MetricDescription>
          </styles.DetailedMetric>
          
          <styles.DetailedMetric>
            <styles.MetricHeader>Scale Capacity</styles.MetricHeader>
            <styles.MetricValue>{performanceData.scale.records} records</styles.MetricValue>
            <styles.MetricDescription>{performanceData.scale.description}</styles.MetricDescription>
          </styles.DetailedMetric>
          
          <styles.DetailedMetric>
            <styles.MetricHeader>Prediction Accuracy</styles.MetricHeader>
            <styles.MetricValue>{performanceData.accuracy.range} typical</styles.MetricValue>
            <styles.MetricDetail>{performanceData.accuracy.average} average across benchmarks</styles.MetricDetail>
            <styles.MetricDescription>{performanceData.accuracy.description}</styles.MetricDescription>
          </styles.DetailedMetric>
          
          <styles.DetailedMetric>
            <styles.MetricHeader>Implementation Speed</styles.MetricHeader>
            <styles.MetricValue>{performanceData.implementation.time}</styles.MetricValue>
            <styles.MetricDetail>{performanceData.implementation.comparison}</styles.MetricDetail>
            <styles.MetricDescription>From data upload to production deployment</styles.MetricDescription>
          </styles.DetailedMetric>
        </styles.DetailedMetricsGrid>
        
        {showHardwareNote && (
          <styles.HardwareNote>
            * Performance metrics measured on standard cloud infrastructure (AWS t3.large equivalent)
          </styles.HardwareNote>
        )}
      </styles.DetailedPanel>
    )
  }

  // Standard variant
  return (
    <styles.StandardPanel className={className}>
      {showTitle && (
        <styles.PanelTitle>{title}</styles.PanelTitle>
      )}
      
      <styles.MetricsGrid>
        <styles.MetricCard>
          <styles.MetricValue>{performanceData.responseTime.range}</styles.MetricValue>
          <styles.MetricLabel>Response Time</styles.MetricLabel>
          <styles.MetricNote>{performanceData.responseTime.percentile}</styles.MetricNote>
        </styles.MetricCard>
        
        <styles.MetricCard>
          <styles.MetricValue>{performanceData.scale.records}</styles.MetricValue>
          <styles.MetricLabel>Records at Scale</styles.MetricLabel>
        </styles.MetricCard>
        
        <styles.MetricCard>
          <styles.MetricValue>{performanceData.accuracy.range}</styles.MetricValue>
          <styles.MetricLabel>Typical Accuracy</styles.MetricLabel>
          <styles.MetricNote>{performanceData.accuracy.average} average</styles.MetricNote>
        </styles.MetricCard>
        
        <styles.MetricCard>
          <styles.MetricValue>{performanceData.implementation.time}</styles.MetricValue>
          <styles.MetricLabel>Implementation</styles.MetricLabel>
          <styles.MetricNote>{performanceData.implementation.comparison}</styles.MetricNote>
        </styles.MetricCard>
      </styles.MetricsGrid>
      
      {showHardwareNote && (
        <styles.HardwareNote>
          Performance metrics measured on standard cloud infrastructure
        </styles.HardwareNote>
      )}
    </styles.StandardPanel>
  )
}

PerformancePanel.propTypes = propTypes
PerformancePanel.defaultProps = defaultProps

export default PerformancePanel